import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  getBonusesList,
  handlePaginate,
  handleSelectAll,
  handleSelectItem,
  updateBonusesBunch,
  getBonusStatusList,
  BonusItem,
} from "store/liveOps/bonuses/slice";
import { bonusesListLimits } from "configs/limits";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";

import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import Pagination from "components/Pagination";
import ActionBtn from "components/buttons/ActionBtn";
import BonusesFilter from "./components/BonusesFilter";

import { Table } from "components/table/Table";
import { GuideButton } from "modals/guides/GuideButton";
import EmptyState from "components/EmptyState/EmptyState";
import { useGuideModal } from "modals/guides/useGuideModal";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import {
  Type,
  TypeLabel,
} from "components/table/components/TypeLabel/TypeLabel";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import ROUTES from "router/routes";
import { RefreshButton } from "components/buttons/RefreshButton";
import { useRefreshList } from "helpers/useRefreshList";
import { isMp4Media } from "helpers/isMp4Media";

const BonusesList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { openGuide } = useGuideModal();
  const permissions = usePermissionHelper();
  const activeBonusId = searchParams.get("entityId");

  const [order, setOrder] = useState<{
    title: string;
    type: string;
    id: string;
    status: string;
  }>({
    title: "none",
    type: "none",
    id: "none",
    status: "none",
  });

  const [search, setSearch] = useState<string>("");

  const [Type, setType] = useState<{ value: number | string; label: string }[]>(
    [],
  );
  const [Status, setStatus] = useState<
    { value: number | string; label: string }[]
  >([]);
  const [Currency, setCurrency] = useState<
    { value: number | string; label: string }[]
  >([]);
  const [currentLimit, setLimit] = useState<number>(bonusesListLimits[0]);

  const list = useAppSelector((store) => store.bonuses.bonusesList);
  const isListFetched = useAppSelector((store) => store.bonuses.isListFetched);
  const totalItems = useAppSelector((store) => store.bonuses.totalItems);
  const totalPages = useAppSelector((store) => store.bonuses.totalPages);
  const activePage = useAppSelector((store) => store.bonuses.activePage);
  const selectedItems = useAppSelector((store) => store.bonuses.selectedItems);
  const processingItemId = useAppSelector(
    (store) => store.bonuses.processingItemId,
  );
  const statusesList = useAppSelector((store) => store.bonuses.statusList);
  const { refresh, isRefreshRequested } = useRefreshList({ sliceName: 'bonuses', isListFetched });
  

  useEffect(() => {
    if (!permissions?.read) return;

    if (!isListFetched) {
      dispatch(
        getBonusesList({
          limit: currentLimit,
          offset: activePage - 1,
          typeId: Type[0]?.value as number,
          statusId: Status[0]?.value as number,
          currencyIds: Currency.map((el) => el.value as string),
          title: search,
          order,
        }),
      );
      dispatch(getBonusStatusList());
    }
  }, [
    permissions?.read,
    isListFetched,
    activePage,
    currentLimit,
    order,
  ]);

  const handleAllClick = () => {
    if (!permissions?.update) return;
    dispatch(handleSelectAll());
  };
  const handleItemClick = (id: number) => {
    if (!permissions?.update) return;
    dispatch(handleSelectItem(id));
  };
  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };
  const handleFilterSearch = (s: string) => {
    setSearch(s);
  };
  const handleApply = () => {
    dispatch(handlePaginate(1));
  };

  const handleActionClick = async (e: string) => {
    if (!permissions?.update) return;

    try {
      await dispatch(
        updateBonusesBunch({
          ids: selectedItems,
          statusId: e,
        }),
      );
    } catch (error) {
      console.log(error, "error");
    }
  };

  const handleOpenEditModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.bonus}&entityId=${id}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.bonus }));
  };
  const handleOpenCreateModal = () => {
    if (!permissions?.create) return;
    navigate(`${location.pathname}?activeModal=${ModalTypes.bonus}`);
    dispatch(openModal({ activeModal: ModalTypes.bonus }));
  };
  const handleOpenDeleteModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.wager}&entityId=${id}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.wager }));
  };

  const handleOrderClick = (key: keyof typeof order) => {
    const current = order[key];
    let next: "asc" | "desc" | "none";

    if (current === "none") next = "asc";
    else if (current === "asc") next = "desc";
    else next = "none";

    const newOrder = {
      [key]: next,
      ...Object.fromEntries(Object.entries(order).filter(([k]) => k !== key)),
    };
    // @ts-ignore
    setOrder(newOrder);
    dispatch(handlePaginate(1));
  };

  const formatTitle = (title?: string) => {
    if (!title) return "-";
    return title.length > 30 ? title.slice(0, 30) + "…" : title;
  };

  const tableData = list.map((item) => ({
    ...item,
    title: formatTitle(item?.title),
  }));

  if (permissions && !permissions.read) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <BonusesFilter
        searchValue={search}
        handleSearch={handleFilterSearch}
        selectedType={Type}
        handleUpdateType={(e) => setType(e)}
        selectedStatus={Status}
        handleUpdateStatus={(e) => setStatus(e)}
        selectedCurrency={Currency}
        handleUpdateCurrency={(e) => setCurrency(e)}
        handleApply={handleApply}
      />
      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box">
            {permissions?.create && (
              <ActionBtn
                txt={t("addBonus")}
                handleClick={handleOpenCreateModal}
              />
            )}
          </div>
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
          <div className="page-list-header--box page-list-header--box-actions">
            <GuideButton onClick={() => openGuide("bonuses")} />
            {permissions?.update && (
              <DefaultDropdown
                onSelect={(e) => {
                  handleActionClick(e as string);
                }}
                placeholder={t("chooseAction")}
                options={
                  statusesList?.map((el) => {
                    return { label: el.title, value: el.id };
                  }) ?? []
                }
                isSmall
                disabled={selectedItems.length === 0}
              />
            )}
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={13} rowsCount={13} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <>
            <div className="game-table-wrap">
              {list.length === 0 && (
                <EmptyState message={t("noBonusesFound")} />
              )}

              {list.length > 0 && (
                <Table
                  data={tableData as Array<BonusItem & { id: number }>}
                  idField="id"
                  order={order}
                  selectedItems={selectedItems}
                  onAllSelect={handleAllClick}
                  onSelect={(id) => handleItemClick(id)}
                  onEdit={(id) => handleOpenEditModal(id)}
                  hasEdit={permissions?.update}
                  hasView={permissions?.read && !permissions?.update}
                  onView={(id) => handleOpenEditModal(id)}
                  activeRowId={activeBonusId}
                  processingId={processingItemId}
                  hasCheckbox={permissions?.update}
                  onSort={(id) => (id ? handleOrderClick(id) : {})}
                  hasDelete={false}
                  customColumns={(row) => {
                    const imageColumn = isMp4Media(row.image)
                      ? {
                          image: (
                            <video
                              src={row.image}
                              muted
                              playsInline
                              preload="metadata"
                              style={{
                                width: 60,
                                height: 40,
                                borderRadius: 4,
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                          ),
                        }
                      : {};

                    return {
                      ...imageColumn,
                      type: <TypeLabel type={row.type as Type} />,
                    };
                  }}
                  columns={{
                    fixedColumns: [
                      "id",
                      "image",
                      "title",
                      "kind",
                      "wagerBalances",
                      "wager",
                      "type",
                      "status",
                      "slug",
                      "segments",
                      "currencyCoverage",
                    ],
                    sortingColumns: {
                      wagerBalances: { value: "wagers" },
                      currencyCoverage: { value: "settings" },
                      activationReadiness: { value: "readiness" },
                    },
                  }}
                />
              )}
            </div>

            {list.length > 0 && (
              <Pagination
                handleLimitClick={(e) => {
                  setLimit(e);
                  dispatch(handlePaginate(1));
                }}
                limitsArray={bonusesListLimits}
                activePage={activePage}
                limit={currentLimit}
                totalItems={totalItems}
                totalPages={totalPages}
                handleNextClick={handleNextClick}
                handlePrevClick={handlePrevClick}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BonusesList;
